import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useIsMobile } from '@sravni/react-utils';
import { Button, Space, Typography } from '@sravni/react-design-system';
import { Controller, ControllerRenderProps, UseFormReturn } from 'react-hook-form';
import { FORM_FIELDS, LABELS, PAGE } from '../../../../../constants';
import { Layout } from '../../../ui/step';
import { GreenMessage } from '../../../ui/step/components/green.message';
import { formatPhone, scrollToForm } from '../../../../../helpers';
import { postMessage, PostMessageType } from '../../../../../helpers/whitelable';
import { SignInParams } from '../../../../../services/identity/identity.types';
import { useSmsAuth } from '../../../../hooks/smsAuth';
import { Col, PhoneInput, Rows, TextInput } from '../../../../landing.common';
import { Row } from '../../common';
import { StepButtons } from '../../../ui/step/components/step.buttons';
import { MainSkeleton } from '../../../../landing.common/main.skeleton';
import { useSelection } from '../../../../../store/reducers/selection';
import { useFormData, useFormStore } from '../../../../../store/selectors/formStore';
import { StepContext } from '../../../creator/step.context';
import { getParamsForAuth } from '../../../../form.common/helpers';
import { EventType, useAnalytics } from '../../../../hooks/analytics';
import styles from './styles.module.scss';

export const SETTINGS = {
    CODE_LENGTH: 4,
    COUNTDOWN_SECONDS: 30,
    ATTEMPTS: 10,
    WRONG_CODE_MESSAGE: 'Не удалось проверить код',
    WRONG_NUMBERS: 'Не удалось проверить цифры',
    FAIL_MESSAGE: 'Не удалось авторизоваться',
};

export interface Props {
    form: UseFormReturn;
    onSubmit?: () => void;
    onBlur?: React.FocusEventHandler<HTMLFormElement>;
    onChange?: React.FormEventHandler<HTMLFormElement>;
}

export const AuthSms: React.FC<Props> = (props) => {
    const { form, onBlur, onChange } = props;
    const { nextStep } = useContext(StepContext);
    const { countdown, inProcess, requestCode, checkCode, errorText, attempts } = useSmsAuth();
    const isMobile = useIsMobile();
    const { page } = useSelection();
    const { withSilentAuth } = useFormStore();
    const formData = useFormData();
    const { pushEvent } = useAnalytics();

    const {
        control,
        watch,
        formState: { errors, dirtyFields: _ },
        trigger,
        setValue,
        getValues,
    } = form;

    const [phone, code = ''] = watch([FORM_FIELDS.PHONE, FORM_FIELDS.SMS_CODE]);

    const [editPhone, setEditPhone] = useState(!phone);

    useEffect(() => {
        focusCodeInput();
        scrollToForm();
        sendSms();
        if (page === PAGE.AVITO) {
            postMessage({
                type: PostMessageType.event,
                label: 'broker_form_num_load',
            });
        }
    }, []);

    const focusCodeInput = () => {
        const codeInput = document.querySelector<HTMLInputElement>(`[name="${FORM_FIELDS.SMS_CODE}"]`);
        if (codeInput) {
            codeInput.focus();
        }
    };

    const onSmsCodeChange =
        (field: ControllerRenderProps): React.FormEventHandler<HTMLInputElement> =>
        (event) => {
            const { currentTarget } = event;
            const { value } = currentTarget;

            if (value.length > 4) {
                return;
            }

            field.onChange(event);

            if (!isMobile && value.length === 4) {
                onCheckCode(value);
            }
        };

    const onCheckCode = async (code: string) => {
        const freshUserId = await checkCode(
            {
                ...getParamsForAuth(formData),
                code,
            },
            withSilentAuth,
        );
        if (!freshUserId) {
            return form;
        }

        if (page === PAGE.AVITO) {
            postMessage({
                type: PostMessageType.event,
                label: 'broker_form_num_submit',
            });
        } else {
            nextStep();
        }
    };

    const onSubmit = () => {
        const code = getValues(FORM_FIELDS.SMS_CODE) || '';
        if (code.length < 4) {
            return;
        }
        onCheckCode(code);
    };

    const sendSms = async () => {
        const phone = getValues(FORM_FIELDS.PHONE);

        if (!phone) {
            setEditPhone(true);
            return;
        }

        const {
            [FORM_FIELDS.FIRST_NAME]: firstName,
            [FORM_FIELDS.MIDDLE_NAME]: middleName,
            [FORM_FIELDS.EMAIL]: email,
        } = formData;

        const name = [firstName, middleName].join(' ');
        const customer = [PAGE.WHITELABEL, PAGE.AVITO].includes(page)
            ? 'Партнерская программа'
            : [PAGE.AGENTS].includes(page)
              ? 'Агентский кабинет'
              : 'Сайт';
        const params: SignInParams = {
            name,
            phone: phone.replace(/\D/g, ''),
            customer,
            productName: 'Подбор кредита',
        };

        if (page == PAGE.AVITO) {
            params.name = name.trim() ? name : 'Пользователь';
        } else {
            params.email = email;
        }

        requestCode(params);
    };

    const onPhoneChanged = async () => {
        const correct = await trigger(FORM_FIELDS.PHONE);
        if (correct) {
            setValue(FORM_FIELDS.SMS_CODE, '');
            setEditPhone(false);
            sendSms();
        }
    };

    const onChangePhoneClick = useCallback(() => {
        pushEvent({
            event: EventType.mainEvent,
            eventAction: 'Изменить номер',
        });
        setEditPhone(true);
    }, []);

    const onRequestCodeClick = useCallback(() => {
        pushEvent({
            event: EventType.mainEvent,
            eventAction: 'Запросить новый код',
        });
        sendSms();
    }, []);

    if (inProcess) {
        return <MainSkeleton shortSkeleton />;
    }

    const headline = ![PAGE.AVITO, PAGE.WHITELABEL].includes(page) ? 'Заявка на кредит' : false;
    return (
        <Layout locked={inProcess} headline={headline} step={1} onBlur={onBlur} onChange={onChange}>
            <Rows>
                {editPhone ? (
                    <>
                        {page == PAGE.AVITO && (
                            <Row>
                                <Col>
                                    <GreenMessage
                                        icon="checkRound"
                                        text="Введите номер — на него придёт смс с решением о выдаче кредита. Телефон узнает только тот банк, в который вы отправите анкету"
                                    />
                                </Col>
                            </Row>
                        )}
                        <Row>
                            <Col>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.PHONE}
                                    render={({ field }) => (
                                        <PhoneInput
                                            {...field}
                                            label={LABELS[FORM_FIELDS.PHONE]}
                                            type="tel"
                                            errorMessage={errors[FORM_FIELDS.PHONE]?.message as string}
                                        />
                                    )}
                                />
                            </Col>
                        </Row>
                        <StepButtons
                            onContinueButtonClick={onPhoneChanged}
                            continueButtonDisabled={countdown > 0}
                            continueButtonText={`Продолжить${countdown > 0 ? ` (${countdown})` : ''}`}
                        />
                    </>
                ) : (
                    <>
                        <Row>
                            <Col>
                                <GreenMessage
                                    icon="checkRound"
                                    text="Подтвердите номер телефона, чтобы мы смогли прислать смс с решением от банка"
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <div className={styles['phone']}>{formatPhone(phone)}</div>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Button
                                    type="button"
                                    color="blue"
                                    variant="text"
                                    className={styles['link']}
                                    onClick={onChangePhoneClick}>
                                    Изменить номер телефона
                                </Button>
                            </Col>
                        </Row>
                        <Row>
                            <Col md-4>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.SMS_CODE}
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            type={isMobile ? 'tel' : 'text'}
                                            inputMode="numeric"
                                            label={LABELS[FORM_FIELDS.SMS_CODE]}
                                            onChange={onSmsCodeChange(field)}
                                            errorMessage={errorText}
                                        />
                                    )}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                {attempts < SETTINGS.ATTEMPTS && (
                                    <div>
                                        {countdown > 0 && (
                                            <span className={styles['timing']}>
                                                Запросить новый код можно через: {countdown}
                                            </span>
                                        )}
                                        {countdown === 0 && (
                                            <Button
                                                type="button"
                                                color="blue"
                                                variant="text"
                                                className={styles['link']}
                                                onClick={onRequestCodeClick}>
                                                Запросить новый код
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </Col>
                        </Row>
                        {isMobile && (
                            <StepButtons
                                onContinueButtonClick={onSubmit}
                                continueButtonDisabled={code.length < 4}
                                continueButtonText="Далее"
                            />
                        )}
                    </>
                )}
            </Rows>

            {page === PAGE.AVITO ? (
                <Space justify={'center'}>
                    <Typography.Text className={'h-color-D60 h-text-center'}>
                        Услуги по подбору кредитных и финансовых продуктов оказываются ООО &quot;Сравни.Ру&quot;.
                    </Typography.Text>
                </Space>
            ) : null}
        </Layout>
    );
};
